/**
 * Chat visuals: the assistant writes JSX-style tags from a fixed catalog
 * (`<Stats>`, `<Chart>`, `<Schedule>`…) inside its markdown, and the chat
 * draws them with Specra components. The tags are PARSED here, never
 * evaluated: a tag outside the catalog, an unknown attribute or a malformed
 * value is an error, and the block falls back to its raw text. Partial input
 * (a reply still streaming) yields a `pending` segment instead of an error.
 *
 * services/shanks/pkg/visuals mirrors this grammar to check a reply before
 * the user sees it; keep the two in step (catalog.json is shared).
 */ function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { checkFormula } from '../../lib/formula';
import catalogJson from './catalog.json';
export const visualCatalog = catalogJson;
const TOP = new Set(visualCatalog.topLevel);
class Incomplete extends Error {
}
class ParseError extends Error {
}
const NAME_RE = /[A-Za-z_][\w-]*/y;
const TAG_RE = /[A-Z][A-Za-z]*/y;
/** A value bound to the reply's inputs: {=fte * rate}. */ export function isFormula(v) {
    return v !== null && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 1 && typeof v['='] === 'string';
}
function walk(node, visit, parent = null) {
    visit(node, parent);
    for (const c of node.children)if (typeof c !== 'string') walk(c, visit, node);
}
/** The inputs a reply declares (in <Inputs> and <Scenario>), with their starting values. */ export function declaredInputs(segments) {
    const out = {};
    for (const s of segments){
        if (s.kind !== 'visual') continue;
        walk(s.node, (n, parent)=>{
            if (n.tag === 'Input' && (parent?.tag === 'Inputs' || parent?.tag === 'Scenario')) {
                const id = String(n.attrs.id ?? '');
                if (id && !(id in out)) out[id] = Number(n.attrs.value);
            }
        });
    }
    return out;
}
/**
 * Every {=formula} and <Output formula> may use only inputs declared at or
 * before its visual, so a reply still streaming never draws a value it
 * cannot compute.
 */ function checkBindings(segments) {
    const declared = new Set();
    return segments.map((s)=>{
        if (s.kind !== 'visual') return s;
        walk(s.node, (n, parent)=>{
            if (n.tag === 'Input' && (parent?.tag === 'Inputs' || parent?.tag === 'Scenario')) {
                declared.add(String(n.attrs.id ?? ''));
            }
        });
        const errors = [];
        walk(s.node, (n, parent)=>{
            for (const [name, v] of Object.entries(n.attrs)){
                const expr = isFormula(v) ? v['='] : n.tag === 'Output' && parent?.tag === 'Scenario' && name === 'formula' ? String(v) : null;
                if (expr === null) continue;
                const check = checkFormula(expr, declared);
                if (!check.ok) errors.push(`<${n.tag} ${name}>: ${check.error}`);
            }
        });
        return errors.length ? {
            kind: 'invalid',
            raw: s.raw,
            errors,
            at: s.at
        } : s;
    });
}
/** Split a reply into markdown and visual segments. */ export function splitVisuals(text) {
    return checkBindings(splitSegments(text));
}
function splitSegments(text) {
    const out = [];
    let md = '';
    let mdAt = 0;
    let i = 0;
    let fence = null;
    const flush = ()=>{
        if (md) out.push({
            kind: 'markdown',
            text: md,
            at: mdAt
        });
        md = '';
    };
    const addMd = (line, at)=>{
        if (!md) mdAt = at;
        md += line;
    };
    while(i < text.length){
        const lineEnd = text.indexOf('\n', i);
        const end = lineEnd < 0 ? text.length : lineEnd + 1;
        const line = text.slice(i, end);
        const trimmed = line.trimStart();
        const fenceMatch = /^(`{3,}|~{3,})/.exec(trimmed);
        if (fenceMatch) {
            if (fence === null) fence = fenceMatch[1];
            else if (trimmed.startsWith(fence)) fence = null;
            addMd(line, i);
            i = end;
            continue;
        }
        const tag = fence === null ? openingTag(trimmed) : null;
        // The reply ends mid tag name ("<Sch"): wait for the rest.
        if (fence === null && end === text.length && lineEnd < 0 && /^<[A-Z]?[A-Za-z]*$/.test(trimmed)) {
            const prefix = trimmed.slice(1);
            const match = visualCatalog.topLevel.find((t)=>t.startsWith(prefix));
            if (match) {
                flush();
                out.push({
                    kind: 'pending',
                    tag: match,
                    raw: trimmed,
                    at: i
                });
                return out;
            }
        }
        if (!tag || !TOP.has(tag)) {
            addMd(line, i);
            i = end;
            continue;
        }
        const start = i + (line.length - trimmed.length);
        try {
            const p = new Parser(text, start);
            const node = p.element();
            const errors = validate(node, null);
            const raw = text.slice(start, p.pos);
            flush();
            out.push(errors.length ? {
                kind: 'invalid',
                raw,
                errors,
                at: start
            } : {
                kind: 'visual',
                node,
                raw,
                at: start
            });
            i = skipRestOfLine(text, p.pos);
        } catch (e) {
            if (e instanceof Incomplete) {
                flush();
                out.push({
                    kind: 'pending',
                    tag,
                    raw: text.slice(start),
                    at: start
                });
                return out;
            }
            // Malformed: show the raw block up to its closing tag, or the line.
            const close = text.indexOf(`</${tag}>`, start);
            const stop = close >= 0 ? close + tag.length + 3 : end;
            flush();
            out.push({
                kind: 'invalid',
                raw: text.slice(start, stop),
                errors: [
                    e instanceof Error ? e.message : String(e)
                ],
                at: start
            });
            i = skipRestOfLine(text, stop);
        }
    }
    flush();
    return out;
}
function openingTag(line) {
    const m = /^<([A-Z][A-Za-z]*)(?=[\s/>]|$)/.exec(line);
    return m ? m[1] : null;
}
function skipRestOfLine(text, pos) {
    const nl = text.indexOf('\n', pos);
    if (nl < 0) return text.length;
    return text.slice(pos, nl).trim() === '' ? nl + 1 : pos;
}
class Parser {
    eof() {
        return this.pos >= this.s.length;
    }
    ws() {
        while(!this.eof() && /\s/.test(this.s[this.pos]))this.pos++;
    }
    need() {
        if (this.eof()) throw new Incomplete();
    }
    element() {
        const at = this.pos;
        this.need();
        if (this.s[this.pos] !== '<') throw new ParseError('expected a tag');
        this.pos++;
        TAG_RE.lastIndex = this.pos;
        const m = TAG_RE.exec(this.s);
        if (!m) throw new ParseError('expected a tag name');
        const tag = m[0];
        this.pos += tag.length;
        const attrs = {};
        for(;;){
            this.ws();
            this.need();
            const c = this.s[this.pos];
            if (c === '/') {
                this.pos++;
                this.need();
                if (this.s[this.pos] !== '>') throw new ParseError(`<${tag}>: expected "/>"`);
                this.pos++;
                return {
                    tag,
                    attrs,
                    children: [],
                    at,
                    keys: []
                };
            }
            if (c === '>') {
                this.pos++;
                break;
            }
            NAME_RE.lastIndex = this.pos;
            const nm = NAME_RE.exec(this.s);
            if (!nm) throw new ParseError(`<${tag}>: unexpected "${c}"`);
            const name = nm[0];
            this.pos += name.length;
            this.ws();
            this.need();
            if (this.s[this.pos] !== '=') {
                attrs[name] = true;
                continue;
            }
            this.pos++;
            this.ws();
            attrs[name] = this.value(tag, name);
        }
        const { children, keys } = this.children(tag);
        return {
            tag,
            attrs,
            children,
            at,
            keys
        };
    }
    children(tag) {
        const out = [];
        const keys = [];
        let text = '';
        let textAt = this.pos;
        const pushText = ()=>{
            if (text.trim()) {
                out.push(dedent(text));
                keys.push(`t${textAt}`);
            }
            text = '';
            textAt = this.pos;
        };
        if (visualCatalog.tags[tag]?.raw) {
            // Source code: "<" and "</div>" are its own, so read to the closing tag.
            const close = `</${tag}>`;
            const end = this.s.indexOf(close, this.pos);
            if (end < 0) throw new Incomplete();
            text = this.s.slice(this.pos, end);
            this.pos = end + close.length;
            pushText();
            return {
                children: out,
                keys
            };
        }
        for(;;){
            this.need();
            const c = this.s[this.pos];
            if (c !== '<') {
                text += c;
                this.pos++;
                continue;
            }
            if (this.s.startsWith('</', this.pos)) {
                const close = `</${tag}>`;
                if (this.s.length - this.pos < close.length && close.startsWith(this.s.slice(this.pos))) {
                    throw new Incomplete();
                }
                if (this.s.startsWith(close, this.pos)) {
                    pushText();
                    this.pos += close.length;
                    return {
                        children: out,
                        keys
                    };
                }
                throw new ParseError(`<${tag}>: closed by the wrong tag`);
            }
            if (/^<[A-Z]/.test(this.s.slice(this.pos, this.pos + 2))) {
                pushText();
                const child = this.element();
                out.push(child);
                keys.push(`n${child.at}`);
                textAt = this.pos;
                continue;
            }
            // A literal "<" in prose ("<$5M").
            text += c;
            this.pos++;
        }
    }
    value(tag, name) {
        this.need();
        const q = this.s[this.pos];
        if (q === '"' || q === "'") {
            this.pos++;
            let v = '';
            for(;;){
                this.need();
                const ch = this.s[this.pos];
                if (ch === '\\' && this.pos + 1 < this.s.length) {
                    v += this.s[this.pos + 1];
                    this.pos += 2;
                    continue;
                }
                this.pos++;
                if (ch === q) return v;
                v += ch;
            }
        }
        if (q === '{') {
            const start = this.pos + 1;
            let depth = 0;
            let inStr = null;
            for(;;){
                this.need();
                const ch = this.s[this.pos];
                if (inStr) {
                    if (ch === '\\') this.pos++;
                    else if (ch === inStr) inStr = null;
                } else if (ch === '"' || ch === "'") inStr = ch;
                else if (ch === '{' || ch === '[') depth++;
                else if (ch === '}' || ch === ']') {
                    depth--;
                    if (depth === 0 && ch === '}') {
                        const expr = this.s.slice(start, this.pos).trim();
                        this.pos++;
                        return parseExpression(expr, tag, name);
                    }
                }
                this.pos++;
            }
        }
        throw new ParseError(`<${tag} ${name}>: a value is "text" or {expression}`);
    }
    constructor(s, pos){
        _define_property(this, "s", void 0);
        _define_property(this, "pos", void 0);
        this.s = s;
        this.pos = pos;
    }
}
/** {expression} holds JSON (numbers, true/false, null, arrays, objects). */ function parseExpression(expr, tag, name) {
    const trimmed = expr.trim();
    // {=fte * rate}: a value bound to the reply's <Inputs>, resolved when drawn.
    if (trimmed.startsWith('=')) return {
        '=': trimmed.slice(1).trim()
    };
    try {
        return JSON.parse(expr);
    } catch  {
        try {
            // Models often write JS-style objects: {a: 1} or single quotes.
            const fixed = expr.replace(/([{,]\s*)([A-Za-z_][\w-]*)\s*:/g, '$1"$2":').replace(/'([^'\\]*)'/g, '"$1"');
            return JSON.parse(fixed);
        } catch  {
            throw new ParseError(`<${tag} ${name}>: {…} must hold JSON`);
        }
    }
}
function dedent(text) {
    const lines = text.replace(/^\n+|\s+$/g, '').split('\n');
    const indents = lines.filter((l)=>l.trim()).map((l)=>/^\s*/.exec(l)?.[0].length ?? 0);
    const cut = indents.length ? Math.min(...indents) : 0;
    return lines.map((l)=>l.slice(cut)).join('\n');
}
const DATE_RE = /^\d{4}-\d{2}(-\d{2})?$/;
/** Check a node and its children against the catalog; [] when valid. */ export function validate(node, parent) {
    const errors = [];
    const spec = visualCatalog.tags[node.tag];
    if (!spec) return [
        `<${node.tag}> is not a visual tag`
    ];
    const allowed = parent === null ? TOP.has(node.tag) : allowedChildren(parent).has(node.tag);
    if (!allowed) {
        errors.push(parent ? `<${node.tag}> cannot go inside <${parent}>` : `<${node.tag}> cannot stand alone`);
    }
    const attrs = spec.attrs ?? {};
    for (const [name, value] of Object.entries(node.attrs)){
        const a = attrs[name];
        if (!a) {
            errors.push(`<${node.tag}> has no "${name}"`);
            continue;
        }
        const why = checkValue(a, value);
        if (why) errors.push(`<${node.tag} ${name}>: ${why}`);
    }
    for (const [name, a] of Object.entries(attrs)){
        if (a.required && !(name in node.attrs)) errors.push(`<${node.tag}> needs ${name}`);
    }
    for (const child of node.children){
        if (typeof child === 'string') {
            if (!spec.text) errors.push(`<${node.tag}> takes tags, not text`);
            continue;
        }
        errors.push(...validate(child, node.tag));
    }
    errors.push(...shapeErrors(node));
    return errors;
}
function allowedChildren(tag) {
    const spec = visualCatalog.tags[tag];
    const out = new Set(spec?.children ?? []);
    if (spec?.nested) for (const t of TOP)out.add(t);
    return out;
}
function checkValue(a, v) {
    if (isFormula(v)) {
        return a.type === 'number' || a.type === 'string' ? null : 'cannot be a {=formula}';
    }
    switch(a.type){
        case 'string':
            return typeof v === 'string' || typeof v === 'number' ? null : 'expects text';
        case 'number':
            return typeof v === 'number' || typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v)) ? null : 'expects a number';
        case 'boolean':
            return typeof v === 'boolean' ? null : 'expects true or false';
        case 'enum':
            return typeof v === 'string' && a.values?.includes(v) ? null : `one of ${a.values?.join(', ')}`;
        case 'tone':
            return typeof v === 'string' && visualCatalog.tones.includes(v) ? null : `one of ${visualCatalog.tones.join(', ')}`;
        case 'date':
            return typeof v === 'string' && DATE_RE.test(v) ? null : 'a date, YYYY-MM-DD or YYYY-MM';
        case 'json':
            return v !== null && typeof v === 'object' ? null : 'expects {[…]} or {{…}}';
        default:
            return null;
    }
}
function kids(node, tag) {
    return node.children.filter((c)=>typeof c !== 'string' && c.tag === tag);
}
const STATE_CODES = new Set('AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY PR'.split(' '));
const IDENT = /^[A-Za-z_][A-Za-z0-9_]*$/;
/** The most code a <Custom> may hold. */ export const CUSTOM_MAX_CODE = 20000;
/** Rules for the interactive and diagram visuals. */ function richShapeErrors(node) {
    const errors = [];
    const need = (tag, child)=>{
        if (kids(node, child).length === 0) errors.push(`<${tag}> needs at least one <${child}>`);
    };
    switch(node.tag){
        case 'Columns':
            {
                const parts = node.children.filter((x)=>typeof x !== 'string');
                if (parts.length < 2 || parts.length > 3) errors.push('<Columns> holds two or three visuals');
                if (parts.some((x)=>typeof x !== 'string' && x.tag === 'Columns')) {
                    errors.push('<Columns> cannot hold <Columns>');
                }
                if (node.children.some((x)=>typeof x === 'string' && x.trim() !== '')) {
                    errors.push('<Columns> holds visuals only; put the text outside');
                }
                break;
            }
        case 'Hero':
            if (kids(node, 'Stat').length > 3) errors.push('<Hero> takes at most three <Stat>s');
            break;
        case 'Checklist':
            need('Checklist', 'Item');
            break;
        case 'Map':
            {
                need('Map', 'State');
                const seen = new Set();
                for (const st of kids(node, 'State')){
                    const code = String(st.attrs.code ?? '').toUpperCase();
                    if (!STATE_CODES.has(code)) errors.push(`<State code="${code}"> is not a US state code`);
                    else if (seen.has(code)) errors.push(`<State code="${code}"> appears twice`);
                    seen.add(code);
                    if (st.attrs.value === undefined && st.attrs.tone === undefined) {
                        errors.push(`<State code="${code}"> needs value or tone`);
                    }
                }
                break;
            }
        case 'Treemap':
            need('Treemap', 'Tile');
            if (kids(node, 'Tile').some((x)=>Number(x.attrs.value) <= 0)) {
                errors.push('<Tile value> must be above 0');
            }
            break;
        case 'Sankey':
            need('Sankey', 'Link');
            for (const l of kids(node, 'Link')){
                if (Number(l.attrs.value) <= 0) errors.push('<Link value> must be above 0');
                if (l.attrs.from === l.attrs.to) errors.push(`<Link> from and to are both "${l.attrs.from}"`);
            }
            break;
        case 'Scenario':
            {
                need('Scenario', 'Input');
                need('Scenario', 'Output');
                const ids = new Set();
                for (const i of kids(node, 'Input')){
                    const id = String(i.attrs.id ?? '');
                    if (!IDENT.test(id)) errors.push(`<Input id="${id}">: use letters, digits and _`);
                    else if (ids.has(id)) errors.push(`<Input id="${id}"> appears twice`);
                    ids.add(id);
                    const v = Number(i.attrs.value);
                    if (i.attrs.min !== undefined && v < Number(i.attrs.min)) errors.push(`<Input id="${id}"> value is below min`);
                    if (i.attrs.max !== undefined && v > Number(i.attrs.max)) errors.push(`<Input id="${id}"> value is above max`);
                }
                for (const o of kids(node, 'Output')){
                    // Inputs may come from an earlier <Inputs>: the reply pass checks the names.
                    const check = checkFormula(String(o.attrs.formula ?? ''));
                    if (!check.ok) errors.push(`<Output label="${o.attrs.label}"> formula: ${check.error}`);
                }
                break;
            }
        case 'Custom':
            {
                const code = node.children.filter((c)=>typeof c === 'string').join('');
                if (!code.trim()) errors.push('<Custom> needs its HTML, CSS and JavaScript inside the tag');
                // Code points, as the server counts them (Go runes), not UTF-16 units.
                if ([
                    ...code
                ].length > CUSTOM_MAX_CODE) {
                    errors.push(`<Custom> code is over ${CUSTOM_MAX_CODE.toLocaleString('en-US')} characters`);
                }
                break;
            }
        case 'Flow':
            if (node.attrs.walkthrough === true && !kids(node, 'Node').some((n)=>n.attrs.step !== undefined)) {
                errors.push('<Flow walkthrough> needs step={n} on its <Node>s');
            }
            break;
    }
    return errors;
}
/** Rules a type check cannot see: a chart's parts match its type, a row fits its table. */ function shapeErrors(node) {
    const errors = richShapeErrors(node);
    if (node.tag === 'Chart' && node.attrs.file !== undefined) {
        if (node.attrs.x === undefined) errors.push('<Chart file> needs x, the column to group by');
        if (node.attrs.agg !== 'count' && node.attrs.y === undefined) {
            errors.push('<Chart file> needs y, the numeric column (or agg="count")');
        }
        if (node.attrs.type === 'stacked' && node.attrs.series === undefined) {
            errors.push('<Chart type="stacked" file> needs series, the column that splits each bar');
        }
        if (node.children.some((c)=>typeof c !== 'string')) {
            errors.push('<Chart file> reads its rows from the file; drop the <Bar>/<Point> rows');
        }
    }
    if (node.tag === 'Action') {
        const kind = node.attrs.kind;
        const href = node.attrs.href;
        if (kind === 'open' && (typeof href !== 'string' || !href.startsWith('/') || href.startsWith('//'))) {
            errors.push('<Action kind="open"> needs href, an app path starting with /');
        }
        if (kind === 'download' && node.attrs.file === undefined) errors.push('<Action kind="download"> needs file');
    }
    if (node.tag === 'Chart' && node.attrs.file === undefined) {
        const type = node.attrs.type;
        const bars = kids(node, 'Bar');
        if (type === 'bar' && (bars.length === 0 || bars.some((b)=>b.attrs.value === undefined))) {
            errors.push('<Chart type="bar"> needs <Bar value={…}> rows');
        }
        if (type === 'stacked') {
            const keys = new Set(kids(node, 'Segment').map((s)=>String(s.attrs.key)));
            if (keys.size === 0) errors.push('<Chart type="stacked"> needs <Segment> keys');
            for (const b of bars){
                const v = b.attrs.values;
                if (!v || typeof v !== 'object' || Array.isArray(v)) {
                    errors.push('<Chart type="stacked"> rows need values={{…}}');
                    break;
                }
                for (const k of Object.keys(v)){
                    if (!keys.has(k)) errors.push(`<Bar values>: "${k}" is not a <Segment> key`);
                }
            }
        }
        if (type === 'line' && kids(node, 'Point').length < 2) errors.push('<Chart type="line"> needs 2+ <Point>s');
    }
    if (node.tag === 'Table') {
        const cols = node.attrs.columns;
        if (!Array.isArray(cols) || cols.length === 0) errors.push('<Table columns> is a list of headers');
        else {
            for (const tr of kids(node, 'Tr')){
                const cells = tr.attrs.cells;
                if (!Array.isArray(cells) || cells.length !== cols.length) {
                    errors.push(`<Tr cells> needs ${cols.length} cells, one per column`);
                    break;
                }
            }
        }
    }
    if (node.tag === 'Span') {
        const { from, to } = node.attrs;
        if (typeof from === 'string' && typeof to === 'string' && from > to) errors.push('<Span>: from is after to');
    }
    if (node.tag === 'Flow') {
        const ids = new Set();
        for (const n of kids(node, 'Node')){
            const id = String(n.attrs.id);
            if (ids.has(id)) errors.push(`<Node id="${id}"> is used twice`);
            ids.add(id);
        }
        for (const e of kids(node, 'Edge')){
            for (const end of [
                'from',
                'to'
            ]){
                const id = String(e.attrs[end]);
                if (!ids.has(id)) errors.push(`<Edge ${end}="${id}"> names no <Node>`);
            }
        }
    }
    if (node.tag === 'OrgTree') {
        const members = kids(node, 'Member');
        const ids = new Set(members.map((m)=>String(m.attrs.id)));
        if (ids.size !== members.length) errors.push('<Member> ids must be unique');
        for (const m of members){
            const parent = m.attrs.parent;
            if (parent !== undefined && !ids.has(String(parent))) {
                errors.push(`<Member parent="${String(parent)}"> names no <Member>`);
            }
        }
        if (members.length && members.every((m)=>m.attrs.parent !== undefined)) {
            errors.push('<OrgTree> needs a top member with no parent');
        }
    }
    if (node.tag === 'DocPage' && node.attrs.quote === undefined && node.attrs.page === undefined) {
        errors.push('<DocPage> needs quote or page');
    }
    return errors;
}
